import { useMemo, useState, useSyncExternalStore } from 'react';
import {
  autoSeries,
  checkTypedTrackingNo,
  numberingOptions,
  watchUpfrontNumber,
  type NumberingOption,
  type UpfrontNumber,
  type UpfrontNumberState,
} from '@ecobox/firebase';
import { useServices } from '../services';
import { useConfigRecords } from './usePickupConfig';

const LOADING: UpfrontNumberState = { status: 'loading' };
const none = (): (() => void) => () => undefined;
const loading = (): UpfrontNumberState => LOADING;

export interface TrackingChoice {
  /** The generated number of the chosen numbering series. */
  state: UpfrontNumberState;
  watch: UpfrontNumber | null;
  options: NumberingOption[];
  seriesId: string;
  why: string | undefined;
  pickSeries: (id: string) => void;
  /** A number typed by hand instead, or null while the generated one is used. */
  custom: { value: string; problem: string | null } | null;
  setCustom: (text: string | null) => void;
}

/**
 * The tracking number of the cargo taken at a pickup stop, shown at the start of the Boxes step
 * (05 §5.2 pickup stop). Reading it uses no number; on a phone with none it asks the server for a
 * range at once while there is signal, so the first pickup on a new phone works. It follows the
 * customer's own numbering series (03 §3.5), another can be picked, and a driver allowed to may
 * type a number by hand (D-27).
 */
export function useTrackingChoice(
  branchId: string | null | undefined,
  customerId: string | null | undefined,
): TrackingChoice {
  const { leases, connectivity } = useServices();
  const { records } = useConfigRecords('numberSeries', branchId !== null && branchId !== undefined);
  const year = new Date().getFullYear();
  const options = useMemo(
    () => (branchId ? numberingOptions(records, branchId, year) : []),
    [records, branchId, year],
  );
  const auto = useMemo(
    () => (branchId ? autoSeries(records, branchId, { customerId }) : { id: '', why: undefined }),
    [records, branchId, customerId],
  );
  const [picked, setPicked] = useState<{ customerId: string; id: string } | null>(null);
  const manual =
    picked && picked.customerId === (customerId ?? '') && options.some((o) => o.id === picked.id)
      ? picked.id
      : null;
  const seriesId = manual ?? auto.id;
  const watch = useMemo(
    () =>
      branchId
        ? watchUpfrontNumber({
            leases,
            connectivity,
            series: 'tracking',
            branchId,
            ...(seriesId && seriesId !== branchId ? { seriesId } : {}),
          })
        : null,
    [leases, connectivity, branchId, seriesId],
  );
  const state = useSyncExternalStore(
    watch?.subscribe ?? none,
    watch?.getSnapshot ?? loading,
    watch?.getSnapshot ?? loading,
  );
  const [typed, setTyped] = useState<string | null>(null);
  return {
    state,
    watch,
    options,
    seriesId,
    why: manual ? undefined : auto.why,
    pickSeries: (id) => setPicked({ customerId: customerId ?? '', id }),
    custom: typed === null ? null : checkTypedTrackingNo(typed),
    setCustom: setTyped,
  };
}
